import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Editor } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';

interface TableInsertDialogProps {
  open: boolean;
  onClose: () => void;
  editor: Editor | null;
}

const MIN_ROWS = 1;
const MAX_ROWS = 10;
const MIN_COLS = 1;
const MAX_COLS = 10;
const DEFAULT_ROWS = 3;
const DEFAULT_COLS = 3;

const stepperIconButtonStyle = {
  border: `1px solid ${palette.divider}`,
  borderRadius: borderRadius.sm,
  padding: spaces.xs,
  backgroundColor: palette.background.paperElevation2,
};

const TableInsertDialog: React.FC<TableInsertDialogProps> = ({ open, onClose, editor }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [cols, setCols] = useState(DEFAULT_COLS);
  const [showError, setShowError] = useState(false);

  const handleRowsChange = (delta: number) => {
    setRows(prev => Math.max(MIN_ROWS, Math.min(MAX_ROWS, prev + delta)));
  };

  const handleColsChange = (delta: number) => {
    setCols(prev => Math.max(MIN_COLS, Math.min(MAX_COLS, prev + delta)));
  };

  const handleInsert = () => {
    if (!editor) {
      setShowError(true);
      return;
    }

    try {
      const success = editor
        .chain()
        .focus()
        .insertTable({ rows, cols, withHeaderRow: true })
        .run();

      if (!success) {
        setShowError(true);
        return;
      }

      // Reset state and close on success
      setRows(DEFAULT_ROWS);
      setCols(DEFAULT_COLS);
      onClose();
    } catch (error) {
      setShowError(true);
    }
  };

  const handleCancel = () => {
    // Reset state on cancel
    setRows(DEFAULT_ROWS);
    setCols(DEFAULT_COLS);
    onClose();
  };

  // Generate preview table
  const renderPreview = () => {
    return (
      <Box
        sx={{
          border: `1px solid ${palette.divider}`,
          borderRadius: borderRadius.md,
          padding: spaces.md,
          backgroundColor: palette.background.paperElevation2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          component='table'
          sx={{
            borderCollapse: 'collapse',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <tbody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({ length: cols }).map((_, colIndex) => (
                  <Box
                    component='td'
                    key={colIndex}
                    sx={{
                      border: `1px solid ${palette.divider}`,
                      padding: '8px',
                      minWidth: '40px',
                      height: '32px',
                      backgroundColor: palette.background.paperElevation2,
                    }}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          handleCancel();
        }}
        maxWidth={false}
        sx={{
          zIndex: 1500,
        }}
        PaperProps={{
          sx: {
            width: '600px',
            maxWidth: '90vw',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: spaces.lg,
            gap: spaces.md,
          }}
        >
          <Typography variant='h5' component='span'>
            {t('Insert Table')}
          </Typography>
          <IconButton
            aria-label='close'
            onClick={handleCancel}
            size='small'
            sx={{
              padding: spaces.xs,
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            paddingX: spaces.lg,
            paddingBottom: spaces.md,
            paddingTop: 0,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.lg }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.md }}>
              <Typography variant='body1' color='text.secondary'>
                {t('Number of Rows')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <IconButton
                  onClick={() => handleRowsChange(-1)}
                  disabled={rows <= MIN_ROWS}
                  size='small'
                  sx={stepperIconButtonStyle}
                >
                  <RemoveIcon />
                </IconButton>
                <Typography variant='subtitle1' sx={{ fontWeight: 500 }}>
                  {rows}
                </Typography>
                <IconButton
                  onClick={() => handleRowsChange(1)}
                  disabled={rows >= MAX_ROWS}
                  size='small'
                  sx={stepperIconButtonStyle}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.md }}>
              <Typography variant='body1' color='text.secondary'>
                {t('Number of Columns')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <IconButton
                  onClick={() => handleColsChange(-1)}
                  disabled={cols <= MIN_COLS}
                  size='small'
                  sx={stepperIconButtonStyle}
                >
                  <RemoveIcon />
                </IconButton>
                <Typography variant='subtitle1' sx={{ fontWeight: 500 }}>
                  {cols}
                </Typography>
                <IconButton
                  onClick={() => handleColsChange(1)}
                  disabled={cols >= MAX_COLS}
                  size='small'
                  sx={stepperIconButtonStyle}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.md }}>
              <Typography variant='body1' color='text.secondary'>
                {t('Preview')}
              </Typography>
              {renderPreview()}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: spaces.xs,
            padding: spaces.lg,
          }}
        >
          <Button
            variant='outlined'
            size='large'
            onClick={handleCancel}
            sx={{ flex: 1 }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant='contained'
            size='large'
            onClick={handleInsert}
            disabled={!editor}
            sx={{ flex: 1 }}
          >
            {t('Insert')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity='error'
          sx={{ width: '100%' }}
        >
          {t('Table could not be inserted. Please try again.')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TableInsertDialog;
